import * as React from 'react';
import styled from 'styled-components';

import { ListView } from '@longline/aqua-ui/controls/ListView';
import { Message } from '@longline/aqua-ui/containers/Message';
import { SecondaryButton } from '@longline/aqua-ui/controls/SecondaryButton';
import { SVG } from '@longline/aqua-ui/svg';
import { NumberFormatter } from '@longline/aqua-ui/formatters/NumberFormatter';
import { CountryFormatter } from '@longline/aqua-ui/formatters/CountryFormatter';

import { IUser } from '../../../api/models/IUser';
import { useUsersContext } from '../IUsersContext';
import { IUserFilter, UserFilter, hasFilter } from '../../../filters/UserFilter';
import { UserInfoBox } from './UserInfoBox';
import { Pane } from '../../../ui/Pane';
import { Can } from '../../../ui/Can';
import { Role } from '../../../api/Permission';
import { RoleBox } from '../../../ui/RoleBox';

const UserList = () => {
  const ctx = useUsersContext();

  const [filter, setFilter] = React.useState<IUserFilter>({});
  
  // Filter users by Q. This is memoized for speed.
  const filteredUsers = React.useMemo(() => {
    return ctx.users.filter(u =>
      ((u.name || "").toLocaleLowerCase().includes((filter.q || "").toLocaleLowerCase()) ||
       (u.email || "").toLocaleLowerCase().includes((filter.q || "").toLocaleLowerCase()))
      && (filter.country == null || u.country === filter.country)
      && (filter.role == null || u.roles.includes(filter.role))
    );
  }, [ctx.users, filter]);  

  const handleCloseInfoBox = () => {
    ctx.setUser(null);
  }

  const renderNoData = () => {
    if(ctx.usersReady == 'failed') {
      return (
        <Message.Error margin onRetry={ctx.loadUsers}>
          Users failed to load.
        </Message.Error>
      );
    } else if (hasFilter(filter)) {
      return (
        <Message.NoFilteredData margin onClear={() => setFilter({})}>
          No users found. Your query may be too restrictive.
        </Message.NoFilteredData>
      )
    } else {
      return (
        <Message.NoData margin>
          There are no users yet.
        </Message.NoData>
      )
    }
  }   

  const handleAdd = () => {
    ctx.setMode('add');
  }
  
  return (
    <>
      <Pane.Heading>
        <UserFilter filter={filter} onChange={setFilter}/>
      </Pane.Heading>
        <Pane.Subheader expandable action={<>
          <Can permission="manage_users">
            <SecondaryButton title="Add user" positive icon={SVG.Icons.Plus} onClick={handleAdd}/>
          </Can>
        </>}>
        Users
      </Pane.Subheader>
      <ListView 
        dark 
        data={filteredUsers} 
        active={ctx.user}
        onClick={(user) => ctx.setUser(user)}
        shadow
        hover
        aux={<UserInfoBox onClose={handleCloseInfoBox}/>}
        total
        singular="user"
        plural="users"
        noData={renderNoData()}
        rowHeight={56}
        ghost={ctx.usersReady == 'loading'}
      >
        <ListView.Column paddingLeft={32} active width={2} name="Name" defaultSort sort="name">
          {(item: IUser) => <Tuple>{item.country ? <CountryFormatter type='flag' value={item.country}/> : <NoCountry/>}<span>{item.name}</span></Tuple>}
        </ListView.Column>
        <ListView.Column active width={1} name="Roles">
          {(item: IUser) => <div style={{ display: 'flex', gap: '4px'}}>{(item.roles || [])
            .sort()
            .filter(r => r !== Role.User)
            .map(r => <RoleBox key={r} role={r}/>)}
          </div>}
        </ListView.Column>
        <ListView.Column active width="130px" align="right" name="Systems" sort="systems_count" reverse>
          {(item: IUser) => <NumberFormatter value={item.systems_count ?? 0} decimals={0}/>}
        </ListView.Column>
      </ListView>
    </>
  )
}

const NoCountry = styled.div`
  width: 14px;
  max-width: 14px;
  min-width: 14px;
`

const Tuple = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

export { UserList }
